import { useEffect, useState } from 'react';
import { Loader2, LayoutTemplate, Check, X } from 'lucide-react';
import { templateApi } from '../../services/api';
import type { ClassifyResponse, ReportTemplate } from '../../types';

interface Props {
  isOpen: boolean;
  classifyResult: ClassifyResponse | null;
  isClassifying: boolean;
  /** Pre-selected template (e.g. an existing manual override). */
  initialTemplateId?: string;
  onConfirm: (templateId: string, templateName: string) => void;
  onCancel: () => void;
}

/**
 * Pass-1 confirmation gate. After dictation the engine recommends a template;
 * the radiologist confirms or changes it before the (expensive) authoring pass.
 * This is the hard reliability gate — a report is never built on the wrong
 * template silently.
 */
export default function ConfirmTemplateModal({
  isOpen,
  classifyResult,
  isClassifying,
  initialTemplateId,
  onConfirm,
  onCancel,
}: Props) {
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [chosenId, setChosenId] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;
    templateApi.list().then(setTemplates).catch(() => setTemplates([]));
  }, [isOpen]);

  // Default the dropdown to the existing manual override, else the recommendation.
  useEffect(() => {
    if (!isOpen) return;
    setChosenId(initialTemplateId || classifyResult?.template_id || '');
  }, [isOpen, initialTemplateId, classifyResult?.template_id]);

  if (!isOpen) return null;

  const chosen = templates.find((t) => t.id === chosenId);
  const chosenName = chosen?.name || classifyResult?.template_name || '';
  const confidencePct =
    classifyResult?.classification_confidence != null
      ? Math.round(classifyResult.classification_confidence * 100)
      : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 dark:bg-black/70" onClick={onCancel} />

      <div className="relative bg-white dark:bg-surface-900 rounded-xl shadow-xl w-full max-w-md mx-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-surface-800">
          <div className="flex items-center gap-2">
            <LayoutTemplate size={18} className="text-primary-600" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Confirm template</h2>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-surface-800"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {isClassifying ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-6 justify-center">
              <Loader2 size={18} className="animate-spin text-primary-500" />
              Detecting study type…
            </div>
          ) : (
            <>
              {/* Recommendation summary */}
              <div className="rounded-lg bg-gray-50 dark:bg-surface-850 border border-gray-200 dark:border-surface-800 p-3 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-500 dark:text-gray-400">Detected study</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {(classifyResult?.exam_category || 'unknown').replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                  {classifyResult?.recommended_source === 'profile' ? (
                    <span className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                      Your onboarded template
                    </span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300">
                      Auto-detected
                    </span>
                  )}
                  {confidencePct != null && <span>{confidencePct}% confidence · {classifyResult?.classification_method}</span>}
                </div>
              </div>

              {/* Template dropdown */}
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Template to use</label>
                <select
                  value={chosenId}
                  onChange={(e) => setChosenId(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-surface-850 border border-gray-300 dark:border-surface-800 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Auto-detect (let the engine choose)</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                      {t.exam_category ? ` — ${t.exam_category.replace(/_/g, ' ')}` : ''}
                    </option>
                  ))}
                </select>
                {chosenId !== (classifyResult?.template_id || '') && chosenId && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Overriding the recommendation with “{chosenName}”.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-surface-800">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800 rounded-lg"
          >
            Cancel
          </button>
          <button
            disabled={isClassifying}
            onClick={() => onConfirm(chosenId, chosenName)}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg transition-colors"
          >
            <Check size={15} />
            Confirm &amp; generate
          </button>
        </div>
      </div>
    </div>
  );
}
